/**
 * Base provider abstract class for ludicrous-gateway
 * Defines common functionality for all provider adapters
 */

import type {
  Provider,
  ProviderInfo,
  ProviderHealth,
  ProviderStats,
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig } from "../types/config.js";
import { getLogger, type Logger } from "../utils/logger.js";

/**
 * Abstract base class for provider implementations
 */
export abstract class BaseProvider implements Provider {
  readonly name: string;
  readonly type: string;
  readonly enabled: boolean;

  protected baseUrl: string;
  protected modelAliases: Map<string, string>;
  protected defaultModel: string | undefined;
  protected logger: Logger;

  protected health: ProviderHealth = {
    status: "healthy",
    lastCheck: new Date(),
    lastSuccess: null,
    lastError: null,
    consecutiveFailures: 0,
  };

  protected stats: ProviderStats = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    totalLatencyMs: 0,
    averageLatencyMs: 0,
    lastRequestTime: null,
  };

  constructor(config: ProviderConfig) {
    this.name = config.name;
    this.type = config.type;
    this.enabled = config.enabled;
    this.baseUrl = config.base_url ?? this.getDefaultBaseUrl();
    this.defaultModel = config.default_model;

    // Build model aliases map
    this.modelAliases = new Map();
    if (config.model_aliases) {
      for (const [from, to] of Object.entries(config.model_aliases)) {
        this.modelAliases.set(from, to);
      }
    }

    this.logger = getLogger().child({ provider: this.name });
  }

  /**
   * Get the default base URL for this provider type
   * Must be implemented by subclasses
   */
  protected abstract getDefaultBaseUrl(): string;

  /**
   * Send a non-streaming request
   * Must be implemented by subclasses
   */
  abstract sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse>;

  /**
   * Send a streaming request
   * Must be implemented by subclasses
   */
  abstract sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void>;

  /**
   * Check provider health
   * Can be overridden by subclasses for custom health checks
   */
  async healthCheck(): Promise<boolean> {
    // Default implementation just checks if we can reach the base URL
    // Subclasses can override for more specific checks
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(this.baseUrl, {
        method: "HEAD",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      this.recordHealthCheck(true);
      return response.ok || response.status < 500;
    } catch (error) {
      this.recordHealthCheck(false, error instanceof Error ? error.message : "Unknown error");
      return false;
    }
  }

  /**
   * Get provider information
   */
  getInfo(): ProviderInfo {
    return {
      name: this.name,
      type: this.type,
      enabled: this.enabled,
      baseUrl: this.baseUrl,
      health: { ...this.health },
      stats: { ...this.stats },
    };
  }

  /**
   * Resolve model name using aliases
   */
  resolveModel(model: string): string {
    const aliased = this.modelAliases.get(model);
    if (aliased) {
      this.logger.debug(`Model alias: ${model} -> ${aliased}`);
      return aliased;
    }
    return this.defaultModel ?? model;
  }

  /**
   * Record a health check result
   */
  protected recordHealthCheck(success: boolean, errorMessage?: string): void {
    this.health.lastCheck = new Date();

    if (success) {
      this.health.lastSuccess = new Date();
      this.health.consecutiveFailures = 0;
      this.health.status = "healthy";
      this.health.errorMessage = undefined;
    } else {
      this.health.lastError = new Date();
      this.health.consecutiveFailures++;
      this.health.errorMessage = errorMessage;

      if (this.health.consecutiveFailures >= 3) {
        this.health.status = "unhealthy";
      } else {
        this.health.status = "degraded";
      }
    }
  }

  /**
   * Record a request result
   */
  protected recordRequest(success: boolean, latencyMs: number): void {
    this.stats.totalRequests++;
    this.stats.lastRequestTime = new Date();
    this.stats.totalLatencyMs += latencyMs;
    this.stats.averageLatencyMs = this.stats.totalLatencyMs / this.stats.totalRequests;

    if (success) {
      this.stats.successfulRequests++;
      this.recordHealthCheck(true);
    } else {
      this.stats.failedRequests++;
      this.recordHealthCheck(false);
    }
  }

  /**
   * Build headers for API request
   * Can be overridden by subclasses
   */
  protected buildHeaders(_context: ProviderRequestContext): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  /**
   * Transform request body if needed
   * Can be overridden by subclasses
   */
  protected transformRequest(request: MessagesRequest): unknown {
    return request;
  }

  /**
   * Transform response if needed
   * Can be overridden by subclasses
   */
  protected transformResponse(response: unknown): MessagesResponse {
    return response as MessagesResponse;
  }
}
